import { Button, CountBird } from '@league/ui';
import type { LeagueSheetResponse, LeagueSheetTable } from '@league/shared/contract';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, apiFetch } from '../../lib/api';
import { leagueDate, leagueTime } from '../../lib/when';
import { TableScoreCard } from '../player/ScoreCard';

/**
 * WHERE THE QR ON A LEAGUE'S SCORE SHEET LANDS — owner, 2026-10-15 (Courtney). The sheet is generic
 * for the league: no table number and no names printed, so a spilled or lost one can be swapped for
 * any other. Any player at the table scans it once the games are done; nobody signs in. This page
 * finds tonight's session, the player picks their table by the names at it, and enters the table's scores —
 * on the same card a table's own sheet opens, handed in for the director to approve.
 *
 * Two parts after `/score/`, where a table's sheet has three, so the two addresses never collide.
 */
export const LEAGUE_SHEET_PATH = /^\/score\/([0-9a-f-]{36})\/([A-Za-z0-9_-]{10,64})\/?$/i;

export function leagueSheetFrom(pathname: string): { leagueId: string; key: string } | null {
  const match = LEAGUE_SHEET_PATH.exec(pathname);
  return match ? { leagueId: match[1]!, key: match[2]! } : null;
}

export function LeagueSheetPage({ brand, leagueId, sheetKey }: { brand: string; leagueId: string; sheetKey: string }) {
  const sheet = useQuery({
    queryKey: ['league-sheet', leagueId],
    queryFn: () => apiFetch<LeagueSheetResponse>(`/api/public/league-sheets/${leagueId}/${sheetKey}`),
    retry: false,
    refetchOnWindowFocus: true,
  });
  const [chosen, setChosen] = useState<LeagueSheetTable | null>(null);
  const tonight = sheet.data?.tonight ?? null;
  const missing = sheet.error instanceof ApiError && sheet.error.status === 404;

  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-light bg-paper">
        <div className="mx-auto flex w-full max-w-2xl items-center gap-2 px-4 py-3">
          <CountBird size={72} ground="light" className="shrink-0" />
          <span className="font-display text-lg leading-none">{brand}</span>
        </div>
      </header>
      <main className="mx-auto w-full max-w-2xl flex-1 px-3 py-5 sm:px-5">
        {sheet.data ? (
          <p className="px-1 text-[15px] text-ink-2">
            {sheet.data.leagueName}
            {tonight ? <> &middot; Session {tonight.sessionNumber}</> : null}
          </p>
        ) : null}

        {sheet.isPending ? <p className="mt-6 px-1 text-[15px] text-ink-3">Loading…</p> : null}

        {missing || (sheet.isError && !missing) ? (
          <div className="mt-6 rounded-md border border-light bg-paper p-5">
            <h1 className="font-display text-[22px]">{missing ? 'This score sheet isn’t recognised' : 'Could not open the score sheet'}</h1>
            <p className="mt-2 text-[15px] text-ink-2">{missing ? 'Ask your director.' : 'Check your connection and try again.'}</p>
          </div>
        ) : null}

        {sheet.data && !tonight ? (
          /* Scanned on another day: say when, rather than only "not open". */
          <div className="mt-6 rounded-md border border-light bg-paper p-5">
            <h1 className="font-display text-[22px]">Scores open on the night</h1>
            {sheet.data.next ? (
              <p className="mt-2 text-[15px] text-ink-2">
                Session {sheet.data.next.sessionNumber} is{' '}
                {leagueDate(sheet.data.next.startsAt, sheet.data.next.timeZone, { weekday: 'long', month: 'long', day: 'numeric' })} at{' '}
                {leagueTime(sheet.data.next.startsAt, sheet.data.next.timeZone)}. Scores open from{' '}
                {leagueTime(sheet.data.next.opensAt, sheet.data.next.timeZone)}.
              </p>
            ) : (
              <p className="mt-2 text-[15px] text-ink-2">This league has no more sessions.</p>
            )}
          </div>
        ) : null}

        {tonight && !chosen ? (
          <section className="mt-5">
            <h1 className="px-1 font-display text-[22px]">Which table are you at?</h1>
            {tonight.tables.length === 0 ? (
              <p className="mt-3 px-1 text-[15px] text-ink-2">Tonight&rsquo;s tables aren&rsquo;t set yet. Ask your director.</p>
            ) : (
              <ul className="mt-3 flex flex-col gap-2">
                {tonight.tables.map((table) => (
                  <li key={table.tableNumber}>
                    <button
                      type="button"
                      onClick={() => setChosen(table)}
                      className="flex min-h-[64px] w-full items-center justify-between gap-3 rounded-md border border-light bg-paper px-4 py-3 text-left hover:border-deep"
                    >
                      <span className="min-w-0">
                        <span className="block text-[17px] font-semibold text-ink">Table {table.tableNumber}</span>
                        <span className="block text-[14px] text-ink-2">{table.players.join(', ')}</span>
                      </span>
                      {table.approved ? <span className="shrink-0 text-[13px] text-sage">Approved</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {tonight && chosen ? (
          <>
            <Button variant="secondary" className="mt-3" onClick={() => setChosen(null)}>
              Choose a different table
            </Button>
            <TableScoreCard
              key={chosen.tableNumber}
              endpoint={`/api/public/table-sheets/${tonight.sessionId}/${chosen.tableNumber}/${chosen.key}`}
              queryKey={['table-sheet', tonight.sessionId, String(chosen.tableNumber)]}
              draftKey={`table-sheet-draft:${tonight.sessionId}:${chosen.tableNumber}`}
              draftBelongs={(base) => base.sessionId === tonight.sessionId && base.tableNumber === chosen.tableNumber}
            />
          </>
        ) : null}
      </main>
    </div>
  );
}
